import { Box, LinearProgress, Typography, Chip } from '@mui/material';
import { FileProgress, FileProgressStage, UploadProgressBarsProps } from '../../types';
import { useMemo } from 'react';
import { formatFileSize } from '../../utilities/fileUtils';

// Display threshold for UI purposes only - actual threshold comes from backend
const getDisplayMultipartThreshold = () => 5 * 1024 * 1024; // 5MB

export const UploadProgressBars: React.FC<UploadProgressBarsProps> = ({ progress, items }) => {
  const activeItems = items.filter(item => progress[item.id]);
  
  // Calculate total progress across ALL items
  const totalProgress = useMemo(() => {
    // First, calculate the total size of files that have a File object
    const totalSize = items
      .filter(item => item.file)  // Only include items with files
      .reduce((acc, item) => acc + item.file!.size, 0);  // Safe to use ! after filter
    
    if (totalSize === 0) return { percentage: 0, completed: 0, total: 0, completedFiles: 0 };
    
    // Calculate completed bytes across all items using item IDs
    const completedBytes = items
      .filter(item => item.file && progress[item.id])  // Match by item.id
      .reduce((acc, item) => {
        const itemProgress = progress[item.id];
        const completed = itemProgress?.completed || 0;
        return acc + completed;
      }, 0);
      
    // Count completed files
    const completedFiles = items.filter(item => {
      const itemProgress = progress[item.id];
      const isCompleted = itemProgress && itemProgress.progress >= 100;
      return isCompleted;
    }).length;
    
    const result = {
      percentage: Math.round((completedBytes / totalSize) * 100),
      completed: completedBytes,
      total: totalSize,
      completedFiles: completedFiles
    };
    
    return result;
  }, [progress, items]);

  // Split items by upload method
  const { multipartItems, standardItems } = useMemo(() => {
    const multipart: typeof items = [];
    const standard: typeof items = [];
    
    items.forEach(item => {
      if (item.file && item.file.size > getDisplayMultipartThreshold()) {
        multipart.push(item);
      } else {
        standard.push(item);
      }
    });
    
    return { multipartItems: multipart, standardItems: standard };
  }, [items]);

  // Update stages to match our S3 upload flow
  const stageLabels: Record<FileProgressStage, string> = {
    pending: "Pending",
    hashing: "Calculating Hash",
    presigning: "Getting Upload URL",
    uploading: "Uploading to S3",
    processing: "Server Processing",
    server: "Server Processing"
  };

  const stageColors: Record<FileProgressStage, string> = {
    pending: '#9e9e9e',    // grey
    hashing: '#ff9800',    // orange
    presigning: '#2196f3', // blue
    uploading: '#4caf50',  // green
    processing: '#9c27b0', // purple
    server: '#9c27b0'      // same as processing
  };

  const getUploadMethodChip = (fileSize: number) => {
    const isMultipart = fileSize > getDisplayMultipartThreshold();
    return (
      <Chip
        label={isMultipart ? "Multipart" : "Standard"}
        size="small"
        variant="outlined"
        sx={{
          backgroundColor: isMultipart ? '#e3f2fd' : '#f3e5f5',
          borderColor: isMultipart ? '#2196f3' : '#9c27b0',
          color: isMultipart ? '#1976d2' : '#7b1fa2',
          fontWeight: 'bold',
          fontSize: '0.75rem'
        }}
      />
    );
  };

  return (
    <Box sx={{ 
      width: "100%",
      height: "100vh",
      display: 'flex',
      flexDirection: 'column',
      p: 4,
    }}>
      {/* Total Progress Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" color="textPrimary">
            Overall Progress ({totalProgress.completedFiles}/{items.length} files)
          </Typography>
          <Typography variant="h6" color="textPrimary">
            {totalProgress.percentage}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={totalProgress.percentage}
          sx={{ 
            height: 12, 
            borderRadius: 3,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#4caf50',
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography 
            variant="body2" 
            color="textSecondary"
          >
            {`${(totalProgress.completed / (1024 * 1024)).toFixed(1)} MB / ${(totalProgress.total / (1024 * 1024)).toFixed(1)} MB`}
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary"
          >
            {standardItems.length > 0 && `${standardItems.length} standard`}
            {standardItems.length > 0 && multipartItems.length > 0 && ' • '}
            {multipartItems.length > 0 && `${multipartItems.length} multipart`}
          </Typography>
        </Box>
      </Box>

      {/* Individual Progress Bars */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}>
        {activeItems.map(item => {
          const fileProgress = progress[item.id];
          if (!fileProgress) return null;

          const progressValue = Math.min(Math.max(fileProgress.progress, 0), 100);
          const stageLabel = stageLabels[fileProgress.stage];
          const fileSize = item.file?.size || 0;
          
          return (
            <Box key={item.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Typography variant="body1" color="textSecondary" sx={{ flex: 1 }}>
                    {item.fileName || item.name}
                  </Typography>
                  {getUploadMethodChip(fileSize)}
                </Box>
                <Typography variant="body1" color="textSecondary">
                  {stageLabel}: {Math.round(progressValue)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{ 
                  height: 10, 
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    transition: 'transform 0.2s linear',
                    backgroundColor: stageColors[fileProgress.stage]
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                >
                  {fileProgress.completed > 0 && (
                    `${(fileProgress.completed / (1024 * 1024)).toFixed(1)} MB / ${(fileProgress.total / (1024 * 1024)).toFixed(1)} MB`
                  )}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                >
                  {formatFileSize(fileSize)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}; 